import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { CircularProgress, Box } from '@mui/material';
import './index.css';
import App from './App';
import { store, persistor } from './store/store';
import { AuthProvider } from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import { installApiFetchInterceptor } from './config/apiClient';
import reportWebVitals from './reportWebVitals';

installApiFetchInterceptor();

// Morph (the Tauri viewer) embeds this app in an iframe with ?uiscale=0.8.
// Scaling must happen HERE, inside the document — WKWebView mishandles both
// transform: scale (wrong click coordinates) and zoom (double-applied scale)
// on an ancestor of a cross-origin iframe. Root zoom alone is not enough
// either: WKWebView does not enlarge the layout viewport under root zoom the
// way Chrome does, so the root is also sized explicitly in pixels
// (window / scale) and kept in sync with window resizes. The app shell sizes
// with % through the html/body/#root chain (not 100vh) so it follows.
try {
  const uiscale = parseFloat(new URLSearchParams(window.location.search).get('uiscale') || '');
  if (Number.isFinite(uiscale) && uiscale >= 0.5 && uiscale <= 1.5 && uiscale !== 1) {
    const rootEl = document.documentElement;
    (rootEl.style as CSSStyleDeclaration & { zoom: string }).zoom = String(uiscale);
    const fitRootToViewport = () => {
      rootEl.style.width = `${window.innerWidth / uiscale}px`;
      rootEl.style.height = `${window.innerHeight / uiscale}px`;
    };
    fitRootToViewport();
    window.addEventListener('resize', fitRootToViewport);
  }
} catch {
  // Malformed query string — render unscaled.
}

// ?clickdebug=1 — draw a crosshair dot at every pointerdown's reported
// coordinates. Diagnoses native event-coordinate offsets (e.g. Tauri/WKWebView
// titlebar hit-testing bugs): if the dot appears displaced from the physical
// cursor tip, the events are arriving offset before any app code runs.
try {
  if (new URLSearchParams(window.location.search).get('clickdebug') === '1') {
    window.addEventListener('pointerdown', (e) => {
      const dot = document.createElement('div');
      dot.style.cssText =
        'position:fixed;z-index:99999;pointer-events:none;' +
        `left:${e.clientX - 7}px;top:${e.clientY - 7}px;width:14px;height:14px;` +
        'border:2px solid #ff2d55;border-radius:50%;' +
        'box-shadow:0 0 0 1px #fff, inset 0 0 0 1px #fff;' +
        'transition:opacity 1.5s;opacity:1;';
      document.body.appendChild(dot);
      const label = document.createElement('div');
      label.style.cssText =
        'position:fixed;z-index:99999;pointer-events:none;' +
        `left:${e.clientX + 12}px;top:${e.clientY - 8}px;` +
        'font:11px monospace;color:#ff2d55;text-shadow:0 0 3px #000;transition:opacity 1.5s;';
      label.textContent = `${Math.round(e.clientX)},${Math.round(e.clientY)}`;
      document.body.appendChild(label);
      setTimeout(() => { dot.style.opacity = '0'; label.style.opacity = '0'; }, 1200);
      setTimeout(() => { dot.remove(); label.remove(); }, 3000);
    }, true);
  }
} catch {
  // Diagnostics only — never block rendering.
}

// Loading component for PersistGate
const PersistenceLoader = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    height="100vh"
    flexDirection="column"
    gap={2}
  >
    <CircularProgress />
    <span>Loading saved data...</span>
  </Box>
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <AuthProvider>
      <ProtectedRoute>
        <Provider store={store}>
          <PersistGate loading={<PersistenceLoader />} persistor={persistor}>
            <App />
          </PersistGate>
        </Provider>
      </ProtectedRoute>
    </AuthProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
